import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../CSS/EmployeeCards.css";
import Sidebar from '../Component/Sidebar';
import { fetchEmployeesThunk } from "../features/employees/employeeThunks";
import { fetchTargetsThunk } from "../features/employeeTargets/employeeTargetThunks";

// Skeleton loading component for a single card
const EmployeeCardSkeleton = () => (
  <div className="employee-card skeleton-card">
    <div className="card-top">
      <div className="employee-photo skeleton">
        <div className="skeleton-circle"></div>
      </div>
      <div className="card-actions">
        <div className="skeleton-dots"></div>
      </div>
    </div>
    
    <div className="employee-info">
      <div className="skeleton-text skeleton-title"></div>
      <div className="skeleton-text skeleton-subtitle"></div>
    </div>
    
    <div className="employee-content">
      <div className="employee-details-grid">
        <div className="detail-col">
          <div className="skeleton-text skeleton-label"></div>
          <div className="skeleton-text"></div>
        </div>
        <div className="detail-col">
          <div className="skeleton-text skeleton-label"></div>
          <div className="skeleton-text"></div>
        </div>
      </div>
      
      <div className="employee-contact">
        <div className="contact-item skeleton-text"></div>
        <div className="contact-item skeleton-text"></div>
      </div>
    </div>
  </div>
);

const EmployeeCards = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get data from Redux store
  const { employees, loading: employeesLoading, error: employeesError } = useSelector(state => state.employees);
  const { targets, loading: targetsLoading, error: targetsError } = useSelector(state => state.employeeTargets);
  
  // Local state for filters
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchEmployeesThunk());
    dispatch(fetchTargetsThunk());
  }, [dispatch]);

  // Handlers
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Department name mapping
  const departmentNames = {
    'TECH_BACKEND': 'Tech Team (Backend)',
    'DEVELOPMENT': 'Development Team',
    'HR': 'HR',
    'OPERATIONS': 'Operations',
    'SALES_MARKETING': 'Sales/Marketing',
    'FLOOR_SUPPORT': 'Floor Support'
  };

  // Filter the employees based on status and search input
  const filteredEmployees = employees.filter((emp) => {
    // Status filter
    if (statusFilter !== "All") {
      if (statusFilter === "Active" && !emp.date_of_joining) {
        return false;
      }
      if (statusFilter === "Inactive" && emp.date_of_joining) {
        return false;
      }
    }
    
    // Search filter
    const lowerSearch = searchTerm.toLowerCase();
    
    const fullName = `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.toLowerCase();
    const matchesName = fullName.includes(lowerSearch);
    
    const matchesRole = emp.role?.toLowerCase()?.includes(lowerSearch) || false;
    const matchesEmail = emp.user?.email?.toLowerCase()?.includes(lowerSearch) || false;
    const matchesDepartment = emp.department?.toLowerCase()?.includes(lowerSearch) || false;
    const matchesDepartmentName = departmentNames[emp.department]?.toLowerCase()?.includes(lowerSearch) || false;

    return matchesName || matchesRole || matchesEmail || matchesDepartment || matchesDepartmentName;
  });

  // Example actions handler
  const handleMoreActions = (e, employeeId) => {
    e.stopPropagation(); // Prevent card click event
    // Display options popup or handle actions
    alert(`Actions for employee with ID: ${employeeId}`);
  };

  // Function to determine employee status color
  const getStatusIndicator = (employee) => {
    // Example status logic - customize based on your data
    if (employee.status === "active" || employee.is_active) {
      return "status-active";
    } else if (employee.status === "inactive" || !employee.is_active) {
      return "status-inactive";
    } else {
      return "status-pending";
    }
  };

  // Format date function to display dates like "12 January 2024"
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const date = new Date(dateString);
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  // Render skeleton cards during loading
  const renderSkeletonCards = () => {
    return Array(6).fill(0).map((_, index) => (
      <EmployeeCardSkeleton key={`skeleton-${index}`} />
    ));
  };

  // Error handler
  const renderError = () => (
    <div className="error-container">Error loading data: {employeesError || targetsError}</div>
  );

  // Handle add candidate - redirect to employee/new
  const handleAddCandidate = () => {
    navigate('/employees/new');
  };

  // Handle card click - navigate to edit page
  const handleCardClick = (employeeId) => {
    navigate(`/employees/${employeeId}`);
  };

  return (
    <div className='index-box'>
      <Sidebar />
      <div className="employee-container">
        {/* Header with count and actions */}
        <div className="employee-header-controls">
          <h2 className="employee-title">
            <span className="employee-count">{filteredEmployees.length}</span> Employee
          </h2>
          
          <div className="employee-actions-group">
            <button className="filter-button">
              <svg className="filter-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M10,18H14V16H10V18M3,6V8H21V6H3M6,13H18V11H6V13Z" />
              </svg>
              Filter
            </button>
            <button className="add-candidate-button" onClick={handleAddCandidate}>
              +Add Candidate
            </button>
          </div>
        </div>

        {/* Main content area */}
        <div className="employee-cards">
          {/* Show skeleton loading when loading */}
          {(employeesLoading || targetsLoading) && renderSkeletonCards()}
          
          {/* Show error if any */}
          {(employeesError || targetsError) && !employeesLoading && !targetsLoading && renderError()}
          
          {/* Show actual cards when data is loaded */}
          {!employeesLoading && !targetsLoading && !employeesError && !targetsError && 
            filteredEmployees.map((emp) => (
              <div 
                className="employee-card" 
                key={emp.id} 
                onClick={() => handleCardClick(emp.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-top">
                  {/* Employee photo with status indicator */}
                  <div className="employee-photo">
                    <img 
                      src={emp.image || `https://via.placeholder.com/80?text=${emp.user?.first_name?.charAt(0) || 'U'}`} 
                      alt={`${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`} 
                    />
                    <div className={`employee-status ${getStatusIndicator(emp)}`}></div>
                  </div>
                  
                  {/* More actions button */}
                  <div className="card-actions">
                    <button className="more-button" onClick={(e) => handleMoreActions(e, emp.id)}>•••</button>
                  </div>
                </div>
                
                {/* Employee name and role */}
                <div className="employee-info">
                  <h3 className="employee-name">{`${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`}</h3>
                  <p className="employee-role">{emp.role || "Project Manager"}</p>
                  {emp.department && (
                    <p className="employee-department" style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                      🏢 {departmentNames[emp.department] || emp.department}
                    </p>
                  )}
                </div>
                
                {/* White background content section */}
                <div className="employee-content">
                  {/* Employee department and hire date */}
                  <div className="employee-details-grid">
                    <div className="detail-col">
                      <div className="detail-label">Department</div>
                      <div className="detail-value">{departmentNames[emp.department] || emp.department || "Not Assigned"}</div>
                    </div>
                    <div className="detail-col">
                      <div className="detail-label">Hired Date</div>
                      <div className="detail-value">{formatDate(emp.date_of_joining)}</div>
                    </div>
                  </div>
                  
                  {/* Contact information */}
                  <div className="employee-contact">
                    <div className="contact-item">
                      <span className="contact-icon">✉</span>
                      {emp.user?.email || "Ronald043@gmail.com"}
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">☎</span>
                      {emp.contact_no || "(229) 555-0109"}
                    </div>
                  </div>
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

export default EmployeeCards;